import { isEmpty } from '@jjj/components';
import moment from 'moment';
import actions from './actions';
import { createReducer } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { t } from '../../../../i18n';
import { getRatesAndRulesFeatureFlag } from './constants';
import { formatMultiplier } from './saga';
import { PENALTY_RATES_TABS } from './sections/penaltyRatesSection/penaltyRatesSection';

const initState = {
  editForm: {},
  formInfo: {},
  filteredPositions: [],
  isEdit: false,
  step: 1,
  maxStep: 4,
  loading: false,
  sector: undefined,
  removeConfirmationModal: {
    isVisible: false,
    affected_organizations: null,
    positionIndex: null,
  },
  shouldForceChangePenaltyRateTab: false,
  baseErrors: [],
  daily_hours_before_overtime_starts: null,
  max_days_worked_per_week: null,
  max_hours_per_day: null,
  max_hours_per_week: null,
  weekly_hours_before_overtime_starts: null,
};

export const defaultPenaltyValue = () => {
  return {
    day: 'Monday',
    start_time: '12:00 am',
    end_time: '11:59 pm',
    hourly_fee: 0,
    multiplier: getRatesAndRulesFeatureFlag() ? 125 : 100,
  };
};

export const defaultOvertimeValue = {
  day: 'Monday',
  min_overtime_hours: 0,
  max_overtime_hours: 24,
  multiplier: 1.5,
};

const initialEditData = () => {
  const defaultPenalty = defaultPenaltyValue();

  return {
    name: '',
    summary: '',
    hidden_on_job_boards: false,
    max_hours_per_day: getRatesAndRulesFeatureFlag() ? 12 : null,
    max_hours_per_week: getRatesAndRulesFeatureFlag() ? 38 : null,
    position_ids: [{}],
    penalty_rates_attributes: [{ ...defaultPenalty }],
    current_start_date: moment().toISOString(),
    future_start_date: moment().toISOString(),
    current_penalty_rates_group: [{ ...defaultPenalty }],
    future_penalty_rates_group: [],
    overtime_rates_attributes: [],
  };
};

function formatObject(payload, prop, state) {
  const propsArray = Object.keys(payload[prop]);

  return keyBy(
    propsArray,
    name => name,
    // eslint-disable-next-line no-use-before-define
    name => formatProp(payload[prop], name, state),
  );
}

function validate(attr, value, state) {
  if (attr.match && attr.match('name')) {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', { fieldName: t('dash.admin.sectors.sector_name_label') });
    }
  }

  if (
    attr.match &&
    (attr.match('current_start_date') ||
      (attr.match('future_start_date') && state.editForm.future_penalty_rates_group?.length))
  ) {
    if (isEmpty(value)) {
      return t('dash.validation.required_error', { fieldName: t('dash.admin.sectors.start_date') });
    }
  }

  if (attr.match && attr.match('position_ids')) {
    if (
      state.editForm.position_ids &&
      state.editForm.position_ids.filter(
        // eslint-disable-next-line no-underscore-dangle
        item => item && item.position_id && item.position_id.value === value && !item._destroy,
      ).length
    ) {
      return t('dash.admin.sectors.already_exists_error');
    }
  }

  return '';
}

function formatArray(payload, prop, state) {
  // eslint-disable-next-line no-use-before-define
  return payload[prop].map((item, index) => formatProp(payload[prop], index, state));
}

function formatProp(payload, prop, state) {
  const getProp = () => ({
    name: prop,
    value: payload[prop],
    error: validate(prop, payload[prop], state),
    touched: false,
  });

  if (Array.isArray(payload[prop])) {
    return formatArray(payload, prop, state);
  }

  if (!Array.isArray(payload[prop]) && typeof payload[prop] === 'object' && payload[prop] != null) {
    return formatObject(payload, prop, state);
  }

  return getProp();
}

function onLoad(state) {
  return { ...state, loading: true, isEdit: false };
}

function onLoadSuccess(state, { payload }) {
  const { data, formInfo } = payload;

  return {
    ...state,
    ...data,
    formInfo: {
      ...state.formInfo,
      ...formInfo,
      ...data.formInfo,
    },
    filteredPositions: payload.data.formInfo.positions,
    loading: false,
  };
}

function onLoadPenaltyRatesRequest(state) {
  return { ...state, ratesLoading: true };
}

function onLoadPenaltyRatesSuccess(state, { payload }) {
  const { sector, penaltyRates, tab, addDefaultOne = false } = payload.data;

  const penaltyGroupKey = `${tab}_penalty_rates_group`;

  state.editForm[penaltyGroupKey] = penaltyRates?.map(item => {
    item.multiplier = formatMultiplier(item.multiplier);

    return item;
  });

  const defaultPenaltyRates = addDefaultOne ? [{ ...defaultPenaltyValue() }] : [];

  if (tab === PENALTY_RATES_TABS.CURRENT) {
    sector.current_penalty_rates_group = penaltyRates.length ? penaltyRates : defaultPenaltyRates;
  } else if (tab === PENALTY_RATES_TABS.FUTURE) {
    sector.future_penalty_rates_group = penaltyRates.length ? penaltyRates : [];
  }

  state.editForm[penaltyGroupKey] = formatProp(sector, penaltyGroupKey, state);

  return { ...state, ...payload.data, ratesLoading: false, sector, editForm: state.editForm };
}

function onPrepareDefaultPenaltyRate(state) {
  state.sector.current_penalty_rates_group = [{ ...defaultPenaltyValue() }];

  return {
    ...state,
    sector: state.sector,
  };
}

function onCreate(state) {
  const init = initialEditData();

  const propsArray = Object.keys(init);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(init, prop, state),
  );

  return { ...state, isEdit: true, sector: init, editForm, loading: false, step: 1 };
}

function onEdit(state, { payload }) {
  const init = initialEditData();
  const { step } = state;
  const current_start_date = payload.data.sector.current_start_date || init.current_start_date;
  const future_start_date = payload.data.sector.future_start_date || init.future_start_date;

  const sector = { ...initialEditData, ...payload.data.sector, step, current_start_date, future_start_date };
  const { positions = [] } = sector;

  sector.position_ids = positions.length === 0 ? [{}] : positions;

  if (step === 3) {
    sector.current_penalty_rates_group = sector.current_penalty_rates_group?.length
      ? sector.current_penalty_rates_group
      : [{ ...defaultPenaltyValue }];

    sector.future_penalty_rates_group = sector.future_penalty_rates_group?.length
      ? sector.future_penalty_rates_group
      : [];
  }

  if (step === 4) {
    sector.overtime_rates_attributes = sector.overtime_rates_attributes.length ? sector.overtime_rates_attributes : [];
  }

  const propsArray = Object.keys(sector);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(sector, prop, state),
  );

  return { ...state, isEdit: true, editForm, loading: false };
}

function onAdd(state) {
  return { ...state, loading: true, step: 1 };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, silent } = payload;
  const { editForm } = state;

  if (silent) {
    editForm[attr] = value;
  } else {
    editForm[attr] = {
      value,
      error: validate(attr, value, state),
      name: attr,
      touched: true,
    };
  }

  return { ...state, editForm: { ...editForm } };
}

function getValue(attrs, value, state) {
  if (typeof value === 'object' && value !== null) {
    return keyBy(
      Object.keys(value),
      prop => prop,
      prop => formatProp(value, prop, state),
    );
  }

  return {
    name: attrs.join('.'),
    touched: true,
    error: validate(attrs.join('.'), value, state),
    value,
  };
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value } = payload;
  const editForm = { ...state.editForm };

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];

    const [formField, index, rowField] = attrs;

    if (
      ['current_penalty_rates_group', 'future_penalty_rates_group'].includes(formField) &&
      rowField === '_destroy' &&
      !editForm[formField][index].id
    ) {
      editForm[formField].splice(index, 1);
    } else {
      for (let i = 0; i < attrs.length - 1; i += 1) {
        data = (data || editForm)[attrs[i]];
      }

      data[name] = {
        name: attrs.join('.'),
        touched: true,
        error: validate(attrs.join('.'), value, state),
        value,
      };

      if (
        ['current_penalty_rates_group', 'future_penalty_rates_group'].includes(attrs[0]) &&
        ['day', 'start_time', 'end_time'].includes(attrs[2])
      ) {
        editForm[attrs[0]].map(rate => {
          if (rate.day) {
            rate.day.error = '';
          }

          return rate;
        });
      }

      if (attrs[0] === 'overtime_rates_attributes' && ['day'].includes(attrs[2])) {
        editForm.overtime_rates_attributes.map(rate => {
          rate.day.error = '';

          return rate;
        });
      }
    }
  } else if (attrs.length === 2) {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] = getValue(attrs, value, state);
  } else {
    editForm[attrs[0]] = getValue(attrs, value, state);
  }

  return { ...state, editForm };
}

function onCheckHowManyUnitsInOrganizationSuccess(state, { payload }) {
  const { affected_organizations, positionIndex } = payload;

  return {
    ...state,
    removeConfirmationModal: {
      ...state.removeConfirmationModal,
      isVisible: true,
      affected_organizations,
      positionIndex,
    },
  };
}

function onToggleConfirmationModal(state, { payload }) {
  const { isVisible } = payload;

  return {
    ...state,
    removeConfirmationModal: {
      ...state.removeConfirmationModal,
      isVisible,
    },
  };
}

function onSaveSuccess(state) {
  return { ...state, isEdit: false, loading: true, editForm: {} };
}

function onSaveFail(state, { payload }) {
  return { ...state, isEdit: true, loading: false, ...payload };
}

function onChangeStep(state, { payload }) {
  return { ...state, step: payload };
}

function onCancel(state) {
  return { ...state, isEdit: false, editForm: {} };
}

function onTouch(state, { payload }) {
  const editForm = JSON.stringify(payload.editForm);

  return {
    ...state,
    editForm: JSON.parse(editForm.replace(/"touched":false/g, '"touched":true')),
  };
}

function onClearStore(state) {
  return { ...initState, step: state.step };
}

function onFilterPositions(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    filteredPositions: data,
  };
}

function shouldForceChangePenaltyRateTab(state) {
  return { ...state, shouldForceChangePenaltyRateTab: !state.shouldForceChangePenaltyRateTab };
}

export default createReducer(initState, {
  [actions.ADD]: onAdd,
  [actions.CANCEL]: onCancel,
  [actions.TOUCH]: onTouch,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_STEP]: onChangeStep,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CREATE]: onCreate,
  [actions.EDIT]: onEdit,
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.LOAD_SECTOR_PENALTY_RATES_REQUEST]: onLoadPenaltyRatesRequest,
  [actions.LOAD_SECTOR_PENALTY_RATES_SUCCESS]: onLoadPenaltyRatesSuccess,
  [actions.PREPARE_DEFAULT_PENALTY_RATE]: onPrepareDefaultPenaltyRate,
  [actions.SAVE_FAIL]: onSaveFail,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.CHECK_UNITS_IN_ORGANIZATION_SUCCESS]: onCheckHowManyUnitsInOrganizationSuccess,
  [actions.TOGGLE_CONFIRMATION_MODAL]: onToggleConfirmationModal,
  [actions.FILTER_POSITIONS]: onFilterPositions,
  [actions.SHOULD_FORCE_CHANGE_PENALTY_RATE_TAB]: shouldForceChangePenaltyRateTab,
});
